/**
 * D18 Replies Lite — the officer's reply workspace.
 *
 * The same three tabs as before: the OA paragraph beside its proposed reply, one
 * Need your input list, and the case materials. Drafting and download never
 * wait for an answer. There is no supporting pack and no technical review here:
 * the lock keeps those out of the officer's way.
 *
 * **Stage 4 on Sarvam adds to it and takes nothing away.** A reply drafted in
 * the background carries `drafting_usage.court`, and for such a version the
 * workspace also shows the drafting as it happens, what each reply's checks
 * found (on the reply, even though it has text), the papers the draft was
 * written from, and a Brief to Counsel tab. A version drafted the earlier way
 * has no `court` object and reads exactly as it did. Finalising — sign, and
 * upload what was filed — is offered for all three documents either way.
 */

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  BlockView,
  CourtDraftView,
  DocumentVersionView,
  ReplyQuestionView,
  ReplyReviewView,
} from '../api/client'
import { FINDING_HEADS, asSentence, pleadingToContinue, readFindings } from '../lib/court'
import { readDate } from '../lib/period'
import { CounselBrief } from './counsel-brief'
import { CourtProgress, PapersUsed } from './court-draft'
import { AddCourtPaper, UploadedFiles } from './court-papers'
import { CaseDocuments, ProposalCards } from './reply-evidence'
import { paperShort } from './pleading'
import { FinaliseReplies } from './reply-finalise'
import { UnclearOaText } from './reply-review'

type WorkspaceTab = 'replies' | 'questions' | 'materials' | 'brief' | 'finalise'

export interface D18ActionCounts {
  missingReplies: number
  needsInput: number
  sourceChecks: number
  /** Replies that have text and an error the checks found. Court drafts only. */
  toCheck: number
  /** Replies that have text and a note for the office to confirm, such as a
   *  Hindi passage quoted in English. Court drafts only. */
  toConfirm: number
}

/** A reply the background job has not reached yet, or is writing now. */
const awaited = (block: BlockView | undefined): boolean =>
  block?.draft_state === 'pending' || block?.draft_state === 'drafting'

/**
 * What the checks found on a reply that has text, by level.
 *
 * `check` — an error, or a finding from before levels were recorded (shown as
 * something to check, never dropped). `confirm` — a note for the office. A
 * reply can carry both.
 */
function findingsOn(block: BlockView | undefined): { check: boolean; confirm: boolean } {
  if (!block?.text_hi.trim()) return { check: false, confirm: false }
  const found = readFindings(block)
  return {
    // `draft_failure` on a reply that has text is the job's own line that a
    // check did not pass after the one repair; the server writes it only
    // where an error stands.
    check: found.errors.length > 0 || found.plain.length > 0 || Boolean(block.draft_failure),
    confirm: found.notes.length > 0,
  }
}

export function actionableCounts(
  review: ReplyReviewView | undefined,
  blocks: BlockView[],
  questions: ReplyQuestionView[] | undefined,
  /** True for a reply drafted in the background (Stage 4 on Sarvam). */
  court = false,
): D18ActionCounts {
  const paragraphs = (review?.source?.paragraphs ?? []).filter((p) => p.kind === 'paragraph')
  const replyTo = (id: string) => blocks.find((b) => b.source_paragraph_id === id)
  return {
    // A reply the job has not reached yet is not missing; it is on its way,
    // and the progress strip says so.
    missingReplies: paragraphs.filter((p) => {
      const reply = replyTo(p.id)
      return !reply?.text_hi.trim() && !awaited(reply)
    }).length,
    needsInput: (questions ?? []).filter((q) => !q.answer).length,
    sourceChecks: paragraphs.filter((p) => Boolean(p.uncertainty.trim())).length,
    toCheck: court ? paragraphs.filter((p) => findingsOn(replyTo(p.id)).check).length : 0,
    toConfirm: court ? paragraphs.filter((p) => findingsOn(replyTo(p.id)).confirm).length : 0,
  }
}

export type ReplyStatus =
  | 'Review suggested change'
  | 'Being drafted'
  | 'Not drafted yet'
  | 'Reply missing'
  | 'Needs your input'
  | 'Check before filing'
  | 'Confirm against the original'
  | 'Check OA text'
  | 'Reply ready'

/** One small actionable status per paragraph, most urgent first. */
export function replyStatus(input: {
  hasReply: boolean
  proposal: boolean
  openQuestion: boolean
  unclearText: boolean
  /** The checks found an error on a reply that has text. */
  findings?: boolean
  /** The checks left a note for the office on a reply that has text. */
  toConfirm?: boolean
  /** The background job is writing this reply now, or has not reached it. */
  waiting?: 'drafting' | 'pending' | null
}): ReplyStatus {
  if (input.proposal) return 'Review suggested change'
  if (!input.hasReply && input.waiting === 'drafting') return 'Being drafted'
  if (!input.hasReply && input.waiting === 'pending') return 'Not drafted yet'
  if (!input.hasReply) return 'Reply missing'
  if (input.openQuestion) return 'Needs your input'
  if (input.findings) return 'Check before filing'
  if (input.toConfirm) return 'Confirm against the original'
  if (input.unclearText) return 'Check OA text'
  return 'Reply ready'
}

function ActionSummary({
  counts,
  onTab,
  short = 'OA',
}: {
  counts: D18ActionCounts
  onTab: (tab: WorkspaceTab) => void
  short?: string
}) {
  const ready = !counts.missingReplies && !counts.needsInput
  return (
    <section className="d18-action-summary" aria-label="What needs attention">
      <strong>{ready ? 'Replies ready to review' : 'What needs attention'}</strong>
      <div className="d18-action-list">
        {counts.missingReplies ? (
          <button type="button" className="quiet" onClick={() => onTab('replies')}>
            {counts.missingReplies} {counts.missingReplies === 1 ? 'reply is' : 'replies are'}{' '}
            missing
          </button>
        ) : null}
        {counts.needsInput ? (
          <button type="button" className="quiet" onClick={() => onTab('questions')}>
            {counts.needsInput}{' '}
            {counts.needsInput === 1
              ? 'question needs your input'
              : 'questions need your input'}
          </button>
        ) : null}
        {counts.toCheck ? (
          <button type="button" className="quiet" onClick={() => onTab('replies')}>
            {counts.toCheck} {counts.toCheck === 1 ? 'reply has' : 'replies have'} something to
            check before filing
          </button>
        ) : null}
        {counts.toConfirm ? (
          <button type="button" className="quiet" onClick={() => onTab('replies')}>
            {counts.toConfirm} {counts.toConfirm === 1 ? 'reply has' : 'replies have'} a passage
            to confirm against the original
          </button>
        ) : null}
        {counts.sourceChecks ? (
          <button type="button" className="quiet" onClick={() => onTab('replies')}>
            Check {counts.sourceChecks} unclear {short}{' '}
            {counts.sourceChecks === 1 ? 'paragraph' : 'paragraphs'}
          </button>
        ) : null}
      </div>
      <p className="small muted">You can edit or download the replies at any time.</p>
    </section>
  )
}

/** **Two files, said as two buttons.** The label and the two links had no
 *  styling of their own, so they ran together as one line of text —
 *  *Download repliesWordPDF* — which reads as a single broken word rather than
 *  a heading and a choice. */
function Downloads({ caseId, versionId }: { caseId: string; versionId: string }) {
  return (
    <div className="d18-downloads" aria-label="Download paragraph-wise replies">
      <span className="lbl">Download replies</span>
      <a className="btn" href={api.commentsPath(caseId, versionId)}>
        Word
      </a>
      <a className="btn" href={api.commentsPdfPath(caseId, versionId)}>
        PDF
      </a>
    </div>
  )
}

/**
 * What the checks found on a reply that was written.
 *
 * **Shown although the reply has text.** A failure used to appear only where
 * there was no reply at all, so a reply carrying a date that is in no paper read
 * exactly like one that passed. Every date, order number and quotation is
 * checked against the papers by code; what does not pass is sent back once, and
 * what still does not pass is kept and listed here — never blanked, never
 * hidden (rule 3). The sentences are the server's own. It reports and does not
 * block: nothing on this page is greyed because of it.
 *
 * **Two kinds, said differently.** An error is wrong on its face: *Check
 * before filing*. A note is not a fault — a Hindi passage quoted in English,
 * which only the office can read against the original: *Confirm against the
 * original*. Printed alike, four translated quotations read as four failed
 * replies. The level is the server's.
 *
 * The findings are the open version's own. An accepted edit is checked again
 * by the server, so what is listed is what stands on the reply as it now reads.
 */
function CheckBeforeFiling({ reply }: { reply: BlockView }) {
  const on = findingsOn(reply)
  if (!on.check && !on.confirm) return null
  const found = readFindings(reply)
  // A reply that carries no levelled findings has its plain notes instead:
  // they are shown as things to check, as they were before levels existed.
  const toCheck = [...found.errors.map((finding) => finding.detail), ...found.plain]
  return (
    <>
      {on.check ? (
        <div className="d18-checks" data-testid="reply-checks">
          <strong>{FINDING_HEADS.error}</strong>
          {toCheck.length ? (
            <ul>
              {toCheck.map((detail, index) => (
                <li key={`${index}-${detail}`}>{asSentence(detail)}</li>
              ))}
            </ul>
          ) : null}
          {reply.draft_failure ? (
            <p className="small muted">{asSentence(reply.draft_failure)}</p>
          ) : null}
        </div>
      ) : null}
      {found.notes.length ? (
        <div className="d18-checks note" data-testid="reply-notes">
          <strong>{FINDING_HEADS.note}</strong>
          <ul>
            {found.notes.map((finding, index) => (
              <li key={`${index}-${finding.detail}`}>{asSentence(finding.detail)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  )
}

export function ReplyRows({
  caseId,
  blocks,
  review,
  questions,
  proposalKeys,
  onEdit,
  onQuestion,
  short = 'OA',
  court = false,
  live = false,
  editClosed = '',
}: {
  caseId: string
  blocks: BlockView[]
  review: ReplyReviewView | undefined
  questions: ReplyQuestionView[]
  proposalKeys: Set<string>
  onEdit?: (block: BlockView) => void
  onQuestion: () => void
  short?: string
  /** A reply drafted in the background: its check findings are shown. */
  court?: boolean
  /** The background job is still writing. */
  live?: boolean
  /** Why a reply cannot be edited now, in words. Empty when it can. */
  editClosed?: string
}) {
  const [showUnclear, setShowUnclear] = useState(false)
  const source = review?.source
  if (!source) {
    return (
      <p>
        {live
          ? 'The pleading’s paragraphs are being read. Each paragraph appears here beside its reply as the replies are written.'
          : 'Loading the paragraph-wise replies…'}
      </p>
    )
  }
  const paragraphs = source.paragraphs.filter((p) => p.kind === 'paragraph')
  const unclear = paragraphs.filter((p) => p.uncertainty.trim()).length
  return (
    <section aria-label="Paragraph-wise draft replies" data-testid="reply-rows">
      {unclear ? (
        <div className="d18-source-check">
          <p className="warn">
            {unclear} {short} {unclear === 1 ? 'paragraph was' : 'paragraphs were'} hard to
            read. The replies are still available.
          </p>
          <button
            type="button"
            className="quiet"
            onClick={() => setShowUnclear((open) => !open)}
          >
            {showUnclear ? `Hide unclear ${short} text` : `Review unclear ${short} text`}
          </button>
          {showUnclear ? (
            <UnclearOaText caseId={caseId} fileId={source.source_file_id} />
          ) : null}
        </div>
      ) : null}
      {paragraphs.map((p) => {
        const reply = blocks.find((b) => b.source_paragraph_id === p.id)
        const openQuestion = questions.some((q) => !q.answer && q.paragraph_ids.includes(p.id))
        const hasReply = Boolean(reply?.text_hi.trim())
        const waiting =
          live && awaited(reply) ? (reply?.draft_state as 'drafting' | 'pending') : null
        const status = replyStatus({
          hasReply,
          proposal: reply ? proposalKeys.has(String(reply.order)) : false,
          openQuestion,
          unclearText: Boolean(p.uncertainty.trim()),
          findings: court && findingsOn(reply).check,
          toConfirm: court && findingsOn(reply).confirm,
          waiting,
        })
        return (
          <article key={p.id} className="d18-reply-row">
            <header>
              <h3>
                {short} {p.label}
              </h3>
              {status === 'Needs your input' ? (
                <button type="button" className="chip" onClick={onQuestion}>
                  {status}
                </button>
              ) : (
                <span
                  className={
                    status === 'Reply ready'
                      ? 'chip good'
                      : status === 'Check before filing'
                        ? 'chip warn'
                        : 'chip'
                  }
                >
                  {status}
                </span>
              )}
            </header>
            <div className="d18-reply-columns">
              <section aria-label={`${short} ${p.label} paragraph`}>
                <strong>{short} paragraph</strong>
                <p className="d18-paragraph-text">{p.text}</p>
                {source.source_url ? (
                  <a
                    href={`${source.source_url}#page=${p.first_page}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open {short} PDF ·{' '}
                    {p.first_page === p.last_page
                      ? `page ${p.first_page}`
                      : `pages ${p.first_page}–${p.last_page}`}
                  </a>
                ) : null}
              </section>
              <section aria-label={`Proposed reply to ${short} ${p.label}`}>
                <strong>Proposed reply</strong>
                <p className="d18-paragraph-text">
                  {reply?.text_hi ||
                    (waiting === 'drafting'
                      ? 'This reply is being written now.'
                      : waiting === 'pending'
                        ? 'This reply has not been written yet. It appears here when the drafting reaches it.'
                        : 'No reply was drafted for this paragraph.')}
                </p>
                {reply?.draft_failure && !reply.text_hi.trim() ? (
                  <p className="small muted">Not drafted: {reply.draft_failure}.</p>
                ) : null}
                {court && reply ? <CheckBeforeFiling reply={reply} /> : null}
                {reply && !reply.locked && (onEdit || editClosed) ? (
                  /* Blocked, on screen, with the reason — a button that
                     vanishes while a draft is written teaches nothing. */
                  <button
                    type="button"
                    className="quiet"
                    disabled={Boolean(editClosed) || !onEdit}
                    title={editClosed || undefined}
                    onClick={() => onEdit?.(reply)}
                  >
                    Edit reply
                  </button>
                ) : null}
              </section>
            </div>
            {reply?.basis?.length ? (
              <details className="d18-reply-why">
                <summary>Records used</summary>
                {reply.basis.map((item) => (
                  <p key={item.id}>
                    <strong>
                      {item.document}
                      {item.page ? `, page ${item.page}` : ''}
                    </strong>{' '}
                    — {item.text}
                  </p>
                ))}
              </details>
            ) : null}
          </article>
        )
      })}
    </section>
  )
}

type AnswerMode = 'confirm' | 'correct' | 'unknown' | 'upload' | null

function QuestionCard({
  caseId,
  versionId,
  item,
  short = 'OA',
}: {
  caseId: string
  versionId: string
  item: ReplyQuestionView
  short?: string
}) {
  const client = useQueryClient()
  const [mode, setMode] = useState<AnswerMode>(null)
  const [note, setNote] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['reply-questions', caseId, versionId] })
    void client.invalidateQueries({ queryKey: ['reply-evidence', caseId, versionId] })
    void client.invalidateQueries({ queryKey: ['officer-facts', caseId] })
  }
  const save = useMutation({
    mutationFn: async () => {
      let sourceFileId: string | null = null
      if (mode === 'upload') {
        const receipt = await api.uploadSourceFile(caseId, file!, {
          other_description: 'Document supplied for a paragraph-wise reply',
        })
        sourceFileId = receipt.file.id
      }
      await api.answerReplyQuestion(caseId, versionId, {
        question_id: item.id,
        kind: mode!,
        note: note.trim(),
        source_file_id: sourceFileId,
        preflight: false,
      })
      // A confirmed or corrected fact updates only the affected replies, as
      // suggestions that still need Accept or Reject.
      return mode === 'confirm' || mode === 'correct'
        ? api.reviseReplies(caseId, versionId, item.paragraph_ids)
        : null
    },
    onSuccess: () => {
      setMode(null)
      setNote('')
      setFile(null)
      refresh()
    },
  })
  const update = useMutation({
    mutationFn: () => api.reviseReplies(caseId, versionId, item.paragraph_ids),
    onSuccess: refresh,
  })
  const revision = save.data ?? update.data
  return (
    <article className="d18-question">
      <h3>{item.question}</h3>
      <p className="small muted">
        For {short} {item.paragraph_labels.join(', ')}
      </p>
      {item.answer ? <p className="small">Your answer: {item.answer.note}</p> : null}
      <div className="row">
        {(
          [
            ['confirm', 'Confirm'],
            ['correct', 'Correct or add details'],
            ['unknown', 'I don’t know'],
            ['upload', 'Upload a document'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={mode === value ? 'btn' : 'quiet'}
            onClick={() => setMode(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === 'correct' ? (
        <label className="field">
          What is correct?
          <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
      ) : null}
      {mode === 'upload' ? (
        <input
          aria-label={`Document for: ${item.question}`}
          type="file"
          accept=".pdf,.docx"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
      ) : null}
      {mode ? (
        <button
          type="button"
          className="btn"
          disabled={
            save.isPending ||
            (mode === 'correct' && !note.trim()) ||
            (mode === 'upload' && !file)
          }
          onClick={() => save.mutate()}
        >
          {save.isPending
            ? 'Saving…'
            : mode === 'confirm' || mode === 'correct'
              ? 'Save and update the reply'
              : mode === 'upload'
                ? 'Upload'
                : 'Save'}
        </button>
      ) : null}
      {item.answer?.kind === 'upload' ? (
        <button
          type="button"
          className="quiet"
          disabled={update.isPending}
          onClick={() => update.mutate()}
        >
          {update.isPending ? 'Updating…' : 'Update the reply with this document'}
        </button>
      ) : null}
      {revision ? (
        <p role="status" className="small">
          {revision.proposals
            ? `${revision.proposals} suggested reply ${revision.proposals === 1 ? 'change is' : 'changes are'} ready in Draft replies.`
            : 'No reply needed to change.'}
        </p>
      ) : save.isSuccess ? (
        <p role="status" className="small">
          Saved. Drafting and download remain available.
        </p>
      ) : null}
      {save.error || update.error ? (
        <p role="alert" className="warn">
          {(save.error ?? update.error) instanceof Error
            ? (save.error ?? update.error)!.message
            : 'That did not work. Your saved replies are unchanged.'}
        </p>
      ) : null}
    </article>
  )
}

export function QuestionList({
  caseId,
  versionId,
  questions,
  short = 'OA',
}: {
  caseId: string
  versionId: string
  questions: ReplyQuestionView[]
  short?: string
}) {
  const open = questions.filter((q) => !q.answer)
  const answered = questions.filter((q) => q.answer)
  return (
    <section className="d18-questions" aria-labelledby="questions-title">
      <h2 id="questions-title">Need your input</h2>
      <p className="small muted">
        Only facts that change a reply are asked. Confirm what is correct, correct it, say you
        do not know, or add a document. Drafting and download remain available.
      </p>
      {open.map((item) => (
        <QuestionCard
          key={item.id}
          caseId={caseId}
          versionId={versionId}
          item={item}
          short={short}
        />
      ))}
      {!open.length ? <p>No questions need your answer right now.</p> : null}
      {answered.length ? (
        <details>
          <summary>Answered ({answered.length})</summary>
          {answered.map((item) => (
            <QuestionCard
              key={item.id}
              caseId={caseId}
              versionId={versionId}
              item={item}
              short={short}
            />
          ))}
        </details>
      ) : null}
    </section>
  )
}

export function OfficerFacts({ caseId }: { caseId: string; sourceId?: string | null }) {
  const client = useQueryClient()
  const [statement, setStatement] = useState('')
  const facts = useQuery({
    queryKey: ['officer-facts', caseId],
    queryFn: () => api.listOfficerFacts(caseId),
  })
  const save = useMutation({
    mutationFn: () =>
      api.recordOfficerFact(caseId, {
        statement: statement.trim(),
        basis: 'personal_knowledge',
        reference: '',
        preflight: false,
      }),
    onSuccess: (rows) => {
      client.setQueryData(['officer-facts', caseId], rows)
      setStatement('')
    },
  })
  return (
    <section className="d18-facts" aria-labelledby="case-facts-title">
      <h3 id="case-facts-title">Facts you confirm</h3>
      <p>
        Add a departmental fact that is not in the papers. It is treated as correct when replies
        are drafted or updated.
      </p>
      <label className="field">
        Confirmed fact
        <textarea
          rows={3}
          value={statement}
          onChange={(event) => setStatement(event.target.value)}
          placeholder="Write one clear fact"
        />
      </label>
      {save.error ? (
        <p role="alert" className="warn">
          The fact could not be saved. Your draft is unchanged.
        </p>
      ) : null}
      <button
        type="button"
        className="btn"
        disabled={!statement.trim() || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : 'Add confirmed fact'}
      </button>
      {(facts.data ?? []).length ? (
        <details>
          <summary>Confirmed facts ({facts.data!.length})</summary>
          <ul>
            {facts.data!.map((fact) => (
              <li key={fact.id}>{fact.statement}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  )
}

export function D18Workspace({
  caseId,
  documentType,
  versionId,
  data,
  onVersion,
  onEdit,
}: {
  caseId: string
  documentType?: string
  versionId: string
  data: DocumentVersionView
  onVersion: (id: string) => void
  onEdit?: (block: BlockView) => void
}) {
  const client = useQueryClient()
  const short = paperShort(documentType)
  const register = documentType ?? data.document_type
  const [tab, setTab] = useState<WorkspaceTab>('replies')
  const review = useQuery({
    queryKey: ['reply-review', caseId, versionId],
    queryFn: () => api.getReplyReview(caseId, versionId),
    retry: false,
  })
  const questions = useQuery({
    queryKey: ['reply-questions', caseId, versionId],
    queryFn: () => api.getReplyQuestions(caseId, versionId),
  })
  const evidence = useQuery({
    queryKey: ['reply-evidence', caseId, versionId],
    queryFn: () => api.getVersionEvidence(caseId, versionId),
  })

  const blocks = data.blocks ?? []
  const repliesWritten = blocks.filter((block) => block.text_hi.trim()).length

  // Stage 4 on Sarvam. Null on a version drafted the earlier way, and then
  // nothing below that reads it renders: the screen is as it was.
  //
  // **The open version's own record, and no other.** An accepted edit used to
  // make a version with no drafting record, and this screen went to the
  // document's history for the version that was drafted and read the papers,
  // the cost and the findings off that. The server now carries the court part
  // of the record onto the new version and checks the edited reply again, so
  // what is read here is current and nothing is looked up.
  const usage = data.drafting_usage?.court ? data.drafting_usage : null
  // The version says a queue nobody is reading as well as the court view does;
  // either is enough for the strip to say so.
  const court: CourtDraftView | null = usage?.court
    ? {
        ...usage.court,
        service_unavailable:
          usage.court.service_unavailable ?? data.service_unavailable ?? null,
      }
    : null
  const live = Boolean(court && data.drafting)
  const signed = Boolean(data.issued_on)

  // The replies land a group at a time while the page polls the version. The
  // paragraphs they sit beside and the questions they raise are separate
  // reads, so they are asked for again whenever the job moves on.
  const landed = court ? `${versionId}:${court.state}:${court.paragraphs_written ?? 0}` : ''
  const seen = useRef(landed)
  useEffect(() => {
    if (!landed || seen.current === landed) return
    seen.current = landed
    void client.invalidateQueries({ queryKey: ['reply-review', caseId, versionId] })
    void client.invalidateQueries({ queryKey: ['reply-questions', caseId, versionId] })
    void client.invalidateQueries({ queryKey: ['reply-evidence', caseId, versionId] })
  }, [client, caseId, versionId, landed])

  const items = questions.data?.items ?? []
  const counts = actionableCounts(review.data, blocks, items, Boolean(court))
  const proposalKeys = new Set(
    (evidence.data?.proposals ?? [])
      .filter((proposal) => proposal.state === 'OPEN')
      .map((proposal) => proposal.block_key),
  )
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['reply-evidence', caseId] })
    void client.invalidateQueries({ queryKey: ['reply-questions', caseId] })
  }

  // A draft that stopped is continued by drafting again against the same
  // pleading: the server carries what was written into a new version. Which
  // pleading that is, the draft itself says (`source_file_id`) — it is not
  // guessed from the paragraph map or from the order of the papers.
  const pleadingId = pleadingToContinue(court)
  const resume = useMutation({
    mutationFn: (readAgain: boolean = false) =>
      api.continueReplies(caseId, register, pleadingId as string, readAgain),
    onSuccess: (fresh) => {
      const id = fresh.document?.document_id
      if (!id) return
      client.setQueryData(['version', caseId, id], fresh.document)
      void client.invalidateQueries({ queryKey: ['versions', caseId, register] })
      onVersion(id)
    },
  })

  const editClosed = signed
    ? 'This document is signed and closed to edits. Draft another version to change it.'
    : live
      ? 'Editing opens when the drafting has finished.'
      : ''

  const tabs: [WorkspaceTab, string][] = [
    ['replies', 'Draft replies'],
    ['questions', 'Need your input'],
    ['materials', 'Case materials'],
    // The Brief is drafted from the same papers as a background draft, so it
    // is offered only where there is one.
    ...(court ? ([['brief', 'Brief to Counsel']] as [WorkspaceTab, string][]) : []),
    ['finalise', 'Finalise'],
  ]

  return (
    <section className="d18-workbench" aria-label="Reply drafting workbench">
      {court && court.state !== 'complete' ? (
        /* **Never a spinner alone.** What is happening, counted, in words, with
           the page beneath it usable — and when it stopped, why, and the way on. */
        <CourtProgress
          court={court}
          blocks={blocks}
          onContinue={() => resume.mutate(false)}
          onReadAgain={() => resume.mutate(true)}
          continuing={resume.isPending}
          continueError={resume.error}
          cannotContinue={
            pleadingId
              ? ''
              : 'This version does not say which pleading it answers. Use “Draft another version” at the top of the page and choose it again.'
          }
        />
      ) : null}
      {signed ? (
        <p className="d18-signed-line" role="status" data-testid="d18-signed">
          Signed on <span className="mono">{readDate(data.issued_on)}</span>. This document is
          closed to edits.
        </p>
      ) : null}
      <div className="d18-workspace-head">
        {live ? (
          <p className="d18-action-summary small muted">
            What needs your attention is listed here once the drafting has finished.
          </p>
        ) : (
          <ActionSummary counts={counts} onTab={setTab} short={short} />
        )}
        <Downloads caseId={caseId} versionId={versionId} />
      </div>
      {/* **No cost on this page, or anywhere the officer looks.** The call count,
          the tokens and a rupee figure came off the officer's document on
          18 Sep 2026 (finding L-01: the figure was an estimate that had been
          wrong before), and on 18 Oct 2026 the last place they were drawn — the
          collapsed papers section of a court draft — went too. They are still
          recorded on the version for the team. */}
      <div role="tablist" aria-label="Reply drafting sections">
        {tabs.map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'replies' ? (
        <div role="tabpanel">
          {court ? <PapersUsed caseId={caseId} court={court} /> : null}
          {evidence.data ? (
            <ProposalCards
              caseId={caseId}
              data={evidence.data}
              contentHash={data.content_hash}
              onVersion={onVersion}
              refresh={refresh}
            />
          ) : null}
          <ReplyRows
            caseId={caseId}
            blocks={blocks}
            review={review.data}
            questions={items}
            proposalKeys={proposalKeys}
            onEdit={onEdit}
            onQuestion={() => setTab('questions')}
            short={short}
            court={Boolean(court)}
            live={live}
            editClosed={editClosed}
          />
        </div>
      ) : null}
      {tab === 'questions' ? (
        <div role="tabpanel">
          <QuestionList caseId={caseId} versionId={versionId} questions={items} short={short} />
        </div>
      ) : null}
      {tab === 'materials' ? (
        <div role="tabpanel" className="d18-materials">
          <AddCourtPaper caseId={caseId} documentType={register} />
          <UploadedFiles caseId={caseId} />
          <OfficerFacts caseId={caseId} />
          {/* The earlier engine's own inventory. A background draft lists the
              papers it was written from on the Draft replies tab instead. */}
          {court ? null : <CaseDocuments data={evidence.data} />}
          <Link to={`/cases/${caseId}/papers`}>Manage all case documents</Link>
        </div>
      ) : null}
      {tab === 'brief' && court ? (
        <div role="tabpanel">
          <CounselBrief
            caseId={caseId}
            documentType={register}
            repliesWritten={repliesWritten}
            repliesDrafting={live}
            papers={court.papers ?? []}
          />
        </div>
      ) : null}
      {tab === 'finalise' ? (
        <div role="tabpanel">
          <FinaliseReplies
            caseId={caseId}
            documentType={register}
            versionId={versionId}
            data={data}
            findings={counts.toCheck}
            toConfirm={counts.toConfirm}
          />
        </div>
      ) : null}
    </section>
  )
}
